import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { IQueryContextDatasourceColumn } from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextHelper } from 'src/app/services/query-context/query-context-helper';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { XYChartAxis } from 'src/app/utils/echarts/xy-chart-axis';
import { DatasourceColumnDataLoaderFactoryOptions } from '../../common/data-loaders/datasource-column';
import { DatasourceColumnHelper } from '../../common/data-loaders/datasource-column-helper';
import { BiVizDashboardUsersActivity } from '../../mopa-users/sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

export interface ChartDeviceDailyRevenueByCountryDataLoaderFactoryOptions
    extends
        ICreatedInstanceId,
        IGcpProjectId,
        IQueryCacheService,
        Pick<
            DatasourceColumnDataLoaderFactoryOptions,
            'filterContext' | 'ignoredContextColumnIds'
        > {
    /** Selects Top n and include the rest in 'Other' */
    topN?: number;
}

/** This class generates DataLoader instance for analytics vs store reports differences */
export class ChartDeviceDailyRevenueByCountryDataLoaderFactory extends Factory<
    DataLoader,
    ChartDeviceDailyRevenueByCountryDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string =
        'ChartDeviceDailyRevenueByCountryDataLoaderFactory';

    getInstance(
        options: ChartDeviceDailyRevenueByCountryDataLoaderFactoryOptions
    ) {
        return new DataLoader(options.createdInstanceId, async function (
            rawQueryContext,
            cancellationToken
        ) {
            const queryContext = structuredClone(rawQueryContext);

            // add split property of FIREBASE_COUNTRY column to queryContext
            const firebaseCountryColumn =
                QueryContextHelper.getOrCreateDatasourceColumn(
                    queryContext,
                    'FIREBASE_COUNTRY'
                );

            firebaseCountryColumn.state.isSplitApplied = true;
            // add FIREBASE_COUNTRY to filterContext
            // if filterContext is not true
            if (options.filterContext !== true) {
                options.filterContext = Array.isArray(options.filterContext)
                    ? options.filterContext
                    : [];
                options.filterContext.push('FIREBASE_COUNTRY');
            }

            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap = BiVizDashboardUsersActivity.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                dsColumns.present // context filter functionality
                    .filter((col: IQueryContextDatasourceColumn) =>
                        DatasourceColumnHelper.filterContextFn(col, options)
                    );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const {
                    dateTrunc,
                    splitSelect,
                    splitGroupBy,
                    where,
                    // is_in_context_app_version,
                    // unnestDatasourceColumn,
                    join,
                } = BiVizDashboardUsersActivity.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    options
                    // selectedDayNoSQL
                );

                // const selectFacts = is_in_context_app_version
                //     ? 'ds.date, ds.proper_id, app_version.*,'
                //     : 'ds.*,';
                const getTopNCountriesSQL: (
                    topN?: number
                ) => Promise<string> = async (topN?: number) => {
                    if (topN === undefined) {
                        return '';
                    }

                    const topNCountriesQuery = `
WITH TopN AS (
  SELECT
    ${splitSelect}
    SUM(revenue_net_pln) AS total_revenue_net_pln
  FROM \`${generateTableFullPath(
      options.gcpProjectId,
      datasourceSqlMap.dataset,
      datasourceSqlMap.tableId
  )}\`
  ${join}
  ${where}
  GROUP BY
    split_group
  ORDER BY
    total_revenue_net_pln DESC
  LIMIT ${topN}
)

SELECT
  ${splitGroupBy} total_revenue_net_pln,
  RANK() OVER (ORDER BY total_revenue_net_pln DESC) AS rank
FROM
  \`TopN\`
`;

                    const topNCountriesQueryResponse =
                        await DataLoader.runQuery(
                            topNCountriesQuery,
                            options.queryCacheService
                        );
                    cancellationToken.throwIfCancellationRequested();

                    let topNCountriesSQL = topNCountriesQueryResponse.result
                        .map((row) => `'${row.split_group}'`)
                        .join(', ');
                    topNCountriesSQL = `split_group IN (${topNCountriesSQL})`;
                    return topNCountriesSQL;
                };
                const topNCountriesSQL = await getTopNCountriesSQL(
                    options.topN
                );

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH base AS
(
SELECT 
  ${splitSelect}date
  , SUM(revenue_net_pln) AS revenue_net_pln
  , SUM(revenue_net_usd) AS revenue_net_usd
  , SUM(revenue_gross_pln) AS revenue_gross_pln
  , SUM(revenue_gross_usd) AS revenue_gross_usd
FROM \`${generateTableFullPath(
                    options.gcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                )}\` ds
${join}
${where}
GROUP BY ${splitGroupBy} date
)

SELECT
  ${
      topNCountriesSQL
          ? `IF(${topNCountriesSQL}, split_group, 'Other')  AS split_group`
          : 'split_group'
  }
  , date
  , SUM(revenue_net_pln) AS revenue_net_pln
FROM base
GROUP BY ${splitGroupBy} date
ORDER BY ${splitGroupBy} date ASC
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();

                let data = [...queryResponse.result];
                this.latestData = data;
                bq2echartsformat(data);
                XYChartAxis.fillMissingTimeGapRows(
                    data,
                    queryContext,
                    'date',
                    'day'
                );

                this.emitter.emit({
                    data: this.latestData,
                    queryContext: queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
